import { useCallback, useMemo, useRef, useState } from 'react';

import type { RowDataTransaction } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useSearchParams } from 'react-router-dom';

import {
  useCreatedFertsQuery,
  useFertDonorSchedulesQuery,
  useFertPulledSemenQuery,
} from '@/services/fertilization/hooks';
import { useIncubators } from '@/services/lookups/hooks';

import { FertilizationPanel } from './FertilizationPanel';
import { FertilizationToolbar } from './FertilizationToolbar';
import {
  FertilizationDonorScheduleType,
  PulledSemenType,
  TechType,
} from './types';
import FertilizationContext from './useFertilizationContext';

import './Fertilization.css';

type FertilizationProps = {
  className?: string;
  children: React.ReactNode;
};

const Fertilization = ({ className, children }: FertilizationProps) => {
  const [poolSires, setPoolSires] = useState<boolean>(false);
  const [showTodaysFerts, setShowTodaysFerts] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fertPlateLotNo, setFertPlateLotNo] = useState('');
  const [fertMediaLotNo, setFertMediaLotNo] = useState<Date>();
  const [mineralOilLot, setMineralOilLot] = useState('');
  const [serum, setSerum] = useState('');
  const [QCIsolate, setQCIsolate] = useState('');
  const [EHMMedia, setEHMMedia] = useState<Date>();
  const [SOFMediaLotNo, setSOFMediaLotNo] = useState<Date>();
  const [culturePlateLotNo, setCulturePlateLotNo] = useState('');
  const [cultureIncubatorNo, setCultureIncubatorNo] = useState<
    string | undefined
  >();
  const [procTech, setProcTech] = useState<TechType | null>(null);
  const [moveTech, setMoveTech] = useState<TechType | null>(null);
  const [fertTech, setFertTech] = useState<TechType | null>(null);
  const [fertComments, setFertComments] = useState<number[]>([]);
  const [showReverseSort, setShowReverseSort] = useState<boolean>(false);
  const [selectedSemenRows, setSelectedSemenRows] = useState<PulledSemenType[]>(
    [],
  );
  const [selectedDonorRows, setSelectedDonorRows] = useState<
    FertilizationDonorScheduleType[]
  >([]);
  const [params] = useSearchParams();
  const fertilizationSite = params.get('site');
  const fertilizationDate = params.get('date');

  const pulledSemenRes = useFertPulledSemenQuery({
    donorScheduleDate: fertilizationDate,
    fertSiteId: fertilizationSite,
  });
  const pulledSemenData = pulledSemenRes.data as PulledSemenType[] | undefined;

  const fertilizationDonorScheduleRes = useFertDonorSchedulesQuery({
    donorScheduleDate: fertilizationDate,
    fertSiteId: fertilizationSite,
  });
  const fertilizationDonorScheduleData = fertilizationDonorScheduleRes.data as
    | FertilizationDonorScheduleType[]
    | undefined;

  const createdFertsRes = useCreatedFertsQuery({
    donorScheduleDate: fertilizationDate,
    fertSiteId: fertilizationSite,
  });

  const incubators = useIncubators(fertilizationSite);

  const donorGridRef =
    useRef<AgGridReact<FertilizationDonorScheduleType>>(null);
  const semenGridRef = useRef<AgGridReact<PulledSemenType>>(null);

  const handleResetUse = useCallback(() => {
    const oldData = semenGridRef.current?.api
      .getRenderedNodes()
      .filter((element) => {
        const useField = element.data?.use;
        return useField !== undefined && useField !== null && useField !== '';
      });

    if (!oldData) return;

    for (const node of oldData) {
      if (node.data?.use) {
        const newData = { ...node.data };
        delete (newData as PulledSemenType).use;
        const tx = {
          update: [newData],
        };

        semenGridRef.current?.api.applyTransaction(tx);
      }
    }
  }, [semenGridRef]);

  const handleResetMoflo = useCallback(() => {
    const oldData = semenGridRef.current?.api
      .getRenderedNodes()
      .filter((element) => {
        const moflo = element.data?.moflo;
        return moflo;
      });

    if (!oldData) return;

    for (const node of oldData) {
      if (node.data?.moflo) {
        const newData = { ...node.data };
        delete (newData as PulledSemenType).moflo;
        const tx = {
          update: [newData],
        };

        semenGridRef.current?.api.applyTransaction(tx);
      }
    }
  }, [semenGridRef]);

  const handleResetDonors = useCallback(() => {
    const oldData = donorGridRef.current?.api.getRenderedNodes();

    if (!oldData) return;

    for (const node of oldData) {
      const newData = { ...node.data };
      delete (newData as FertilizationDonorScheduleType).numFerts;
      delete (newData as FertilizationDonorScheduleType).time;
      delete (newData as FertilizationDonorScheduleType).fertComment;
      delete (newData as FertilizationDonorScheduleType).comments;
      delete (newData as FertilizationDonorScheduleType).contamination;
      const tx = {
        update: [newData],
      } as RowDataTransaction<FertilizationDonorScheduleType>;

      donorGridRef.current?.api.applyTransaction(tx);
    }
  }, [donorGridRef]);

  const handleRefresh = useCallback(() => {
    pulledSemenRes.refetch();
    fertilizationDonorScheduleRes.refetch();
    createdFertsRes.refetch();
    semenGridRef.current?.api.setFilterModel(null);
    donorGridRef.current?.api.setFilterModel(null);
    semenGridRef.current?.api.onFilterChanged();
    donorGridRef.current?.api.onFilterChanged();
    semenGridRef.current?.api.deselectAll();
    donorGridRef.current?.api.deselectAll();
    handleResetUse();
    handleResetDonors();
    handleResetMoflo();
    setProcTech(null);
    setMoveTech(null);
    setFertTech(null);
    setFertComments([]);
    setPoolSires(false);
  }, [
    pulledSemenRes,
    fertilizationDonorScheduleRes,
    createdFertsRes,
    handleResetUse,
    handleResetDonors,
    handleResetMoflo,
  ]);

  const handleSelectionChanged = useCallback(() => {
    if (donorGridRef.current && donorGridRef.current.api) {
      setSelectedDonorRows(donorGridRef.current.api.getSelectedRows());
    }
  }, [donorGridRef, setSelectedDonorRows]);
  const updateFilters = useCallback((isPooled: boolean) => {
    const validNodes: PulledSemenType[] = [];
    semenGridRef.current?.api.forEachNode((node) => {
      const useField = node.data?.use;
      const isNotUndefined = useField !== undefined;
      const isNotZero = useField !== '0';
      const isNotNull = useField !== null;
      const isNotEmpty = useField !== '';

      if (isNotUndefined && isNotZero && isNotNull && isNotEmpty) {
        validNodes.push(node.data as PulledSemenType);
      }
    });
    if (validNodes.length === 0) {
      semenGridRef.current?.api.setFilterModel(null);
      donorGridRef.current?.api.setFilterModel(null);
    } else {
      const useSemenTypeNames = Array.from(validNodes).map(
        (node: { semenTypeName: string }) => node.semenTypeName,
      );
      const useSemenNames = Array.from(validNodes).map(
        (node: { semenName: string }) => node.semenName,
      );
      const useNameTypes = Array.from(validNodes).map(
        (node: { name_Type: string }) => node.name_Type,
      );
      if (isPooled === false) {
        semenGridRef.current?.api.setColumnFilterModel('semenTypeName', {
          values: useSemenTypeNames,
        });
        semenGridRef.current?.api.setColumnFilterModel('semenName', {
          values: useSemenNames,
        });
      } else {
        semenGridRef.current?.api.setFilterModel(null);
      }
      donorGridRef.current?.api.setColumnFilterModel('name_Type', {
        values: useNameTypes,
      });
    }
    semenGridRef.current?.api.onFilterChanged();
    donorGridRef.current?.api.onFilterChanged();
  }, []);

  const value = useMemo(
    () => ({
      poolSires,
      setPoolSires,
      isLoading,
      setIsLoading,
      showTodaysFerts,
      setShowTodaysFerts,
      pulledSemenData,
      fertilizationDonorScheduleData,
      fertPlateLotNo,
      setFertPlateLotNo,
      fertMediaLotNo,
      setFertMediaLotNo,
      mineralOilLot,
      setMineralOilLot,
      serum,
      setSerum,
      QCIsolate,
      setQCIsolate,
      EHMMedia,
      setEHMMedia,
      SOFMediaLotNo,
      setSOFMediaLotNo,
      culturePlateLotNo,
      setCulturePlateLotNo,
      cultureIncubatorNo,
      setCultureIncubatorNo,
      procTech,
      setProcTech,
      moveTech,
      setMoveTech,
      fertTech,
      setFertTech,
      donorGridRef,
      semenGridRef,
      selectedSemenRows,
      setSelectedSemenRows,
      selectedDonorRows,
      setSelectedDonorRows,
      fertComments,
      setFertComments,
      incubators,
      handleRefresh,
      updateFilters,
      handleSelectionChanged,
      showReverseSort,
      setShowReverseSort,
    }),
    [
      poolSires,
      isLoading,
      showTodaysFerts,
      pulledSemenData,
      fertilizationDonorScheduleData,
      fertPlateLotNo,
      fertMediaLotNo,
      mineralOilLot,
      serum,
      QCIsolate,
      EHMMedia,
      SOFMediaLotNo,
      culturePlateLotNo,
      cultureIncubatorNo,
      procTech,
      moveTech,
      fertTech,
      selectedSemenRows,
      selectedDonorRows,
      fertComments,
      incubators,
      handleRefresh,
      updateFilters,
      handleSelectionChanged,
      showReverseSort,
    ],
  );

  return (
    <div className={`common-page ${className}`.trim()}>
      <FertilizationContext.Provider value={value}>
        {children}
      </FertilizationContext.Provider>
    </div>
  );
};

const Root = Fertilization;
const Toolbar = FertilizationToolbar;
const Panel = FertilizationPanel;
export {
  Fertilization,
  FertilizationToolbar,
  FertilizationPanel,
  Root,
  Toolbar,
  Panel,
};

export type { FertilizationProps };
